import { Checkbox, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import useUserLogged from 'src/hooks/useUserLogged';
import { useLogoutMutation } from 'src/redux/api/auth';
import { useUpdateUserMutation } from 'src/redux/api/profile';
import { AppDispatch } from 'src/redux/dispatch';
import { getAuth, logoutCompleted, logoutFromUser } from 'src/redux/slices/auth';
import { getInventory, setInventory } from 'src/redux/slices/inventory';
import useError, { reportError } from 'src/screens/error/useError';
import fillUserFields from 'src/screens/profile/fillUserFields';

import { ContinueButton, RejectButton } from './Styles';

type Props = {
  onClose: () => void;
}

const BetaModal = ({onClose}: Props) => {
  const {t} = useTranslation();
  const [ accepted, setAccepted ] = useState(false);
  const auth = useSelector(getAuth);
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const { isSuperAdminOnlyLogged } = useUserLogged();
  const { setError } = useError();
  const dispatch = useDispatch<AppDispatch>();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const navigate = useNavigate();
  const inventory = useSelector(getInventory);

  useEffect(() => {
    setCurrentUser(auth.currentUser);
  }, [auth.currentUser]);
  
  if (
    !currentUser ||
    (currentUser && (
      currentUser.dms_beta_accepted ||
      currentUser.dms_beta_accepted === null ||
      currentUser.dms_beta_accepted === undefined
    ) ||
    isSuperAdminOnlyLogged))
  {
    return null;
  }

  const handleAccept = async () => {
    if (!currentUser) return;

    try {
      const newFields = fillUserFields(['dms_beta_accepted'], [accepted ? 1 : 0], currentUser);
      await updateUser({userId: currentUser.id, newUser: newFields});
      onClose();
    } catch(e) {
      setError(reportError(e));
    }
  }

  const handleReject = async () => {
    if (auth.superAdminToken) {
      dispatch(logoutFromUser());
      dispatch(setInventory({...inventory, data: []}));
      navigate(ROUTES.LOGIN);
    } else {
      logout({})
        .unwrap()
        .then(() => {
          dispatch(logoutCompleted());
          dispatch(setInventory({...inventory, data: []}));
        }).catch(e => console.log(e));
    }
  };
    
  return (
    <Modal
      closable={false}
      open={true}
      title={<Trans t={t}>{t('loginScreen:accept_beta_title')}</Trans>}
      width='400px'
      footer={null}
      className='beta-modal'
    >
      <p>
        {t('loginScreen:accept_beta_description')}
      </p>
      <Checkbox
        checked={accepted}
        style={{ marginTop: 10}}
        onChange={(value) => {
        setAccepted(value.target.checked);
      }}>
        {t('loginScreen:accept_beta_terms')}
      </Checkbox>

      <ContinueButton
        type="primary"
        disabled={!accepted || isLoading}
        onClick={handleAccept}
        loading={isLoading}
      >
        {t('buttons:continue')}
      </ContinueButton>

      <RejectButton
        type="default"
        disabled={isLoading}
        onClick={handleReject}
        loading={isLoggingOut}
      >
        {t('buttons:reject_and_logout')}
      </RejectButton>
    </Modal>
  )
};

export default BetaModal;
